import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface RegulatoryUpdate {
  id: string;
  title: string;
  country: string;
  date: string;
  type: 'new' | 'updated' | 'alert';
  description: string;
}

interface RegulatoryUpdatesProps {
  updates: RegulatoryUpdate[];
}

const RegulatoryUpdates = ({ updates }: RegulatoryUpdatesProps) => {
  const getUpdateTypeStyle = (type: string) => {
    switch (type) {
      case 'new':
        return 'bg-success/10 text-success border-success';
      case 'updated':
        return 'bg-primary/10 text-primary border-primary';
      case 'alert':
        return 'bg-destructive/10 text-destructive border-destructive';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getUpdateIcon = (type: string) => {
    switch (type) {
      case 'new':
        return 'SparklesIcon';
      case 'updated':
        return 'ArrowPathIcon';
      case 'alert':
        return 'BellAlertIcon';
      default:
        return 'InformationCircleIcon';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      <div className="mb-6">
        <h3 className="font-headline font-bold text-xl text-foreground mb-2">
          Regulatory Updates & Notifications
        </h3>
        <p className="font-body text-sm text-muted-foreground">
          Stay informed about the latest regulatory changes across our coverage areas
        </p>
      </div>

      <div className="space-y-4">
        {updates.map((update) => (
          <div
            key={update.id}
            className="p-4 bg-background border border-border rounded-lg hover:shadow-elevation-1 transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start space-x-3 flex-1">
                <div
                  className={`p-2 rounded-lg border ${getUpdateTypeStyle(
                    update.type
                  )}`}
                >
                  <Icon name={getUpdateIcon(update.type) as any} size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="font-headline font-bold text-base text-foreground mb-1">
                    {update.title}
                  </h4>
                  <div className="flex items-center space-x-3 text-xs font-body text-muted-foreground mb-2">
                    <span className="flex items-center space-x-1">
                      <Icon name="MapPinIcon" size={14} />
                      <span>{update.country}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="CalendarIcon" size={14} />
                      <span>{update.date}</span>
                    </span>
                  </div>
                </div>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-body font-semibold border ${getUpdateTypeStyle(
                  update.type
                )}`}
              >
                {update.type.toUpperCase()}
              </span>
            </div>
            <p className="font-body text-sm text-muted-foreground leading-relaxed pl-14">
              {update.description}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <button className="w-full px-6 py-3 bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark rounded-lg font-cta font-semibold text-sm text-primary hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center space-x-2">
          <Icon name="BellIcon" size={18} />
          <span>Subscribe to Regulatory Updates</span>
        </button>
      </div>
    </div>
  );
};

export default RegulatoryUpdates;